import moment from 'moment';
import { IAdminAclGroup } from '../app/admins/components/AdminAclGroups';
import { IBrandAclGroup } from '../app/brand-access-right/types';

export const formatDate = (date?: string) => {
    if (!date) return '';
    const m = moment(date);
    return m.isValid() ? m.format('DD.MM.YYYY.') : 'N/A';
};

export function hasAccessRight(acl: string) {
    let aclArr = '';
    aclArr = localStorage.getItem('adminAcl') ?? '';
    return aclArr.includes(acl);
}

// Names of the contract and offer statuses that are not obvious from the enum value
const STATUS_LABELS: Record<string, string> = {
    pre_annex: 'Pre-amendment',
    annexed: 'Amended',
};

export const getStatusLabel = (status: string) => STATUS_LABELS[status] ?? getCorrectEnums(status);

export const getCorrectEnums = (param: string) => {
    let res = '';
    if (param.includes('_')) {
        const arrWords = param.split('_');
        arrWords.map((item: string) => (res = res + ' ' + item.charAt(0).toUpperCase() + item.slice(1)));
    } else {
        res = param.charAt(0).toUpperCase() + param.slice(1);
    }

    return res;
};

export const getCorrectSocialUrl = (name: string, handle: string, externalLink?: string) => {
    switch (name) {
        case 'instagram':
            return 'https://instagram.com/' + handle;
        case 'youtube':
            return 'https://youtube.com/@' + handle;
        case 'facebook':
            return 'https://facebook.com/' + handle;
        case 'tiktok':
            return 'https://tiktok.com/@' + handle;
        case 'twitter':
            return 'https://twitter.com/' + handle;
        case 'twitch':
            return 'https://twitch.com/' + handle;
        case 'podcast':
        case 'blog':
        case 'linkedin':
            return externalLink;
        default:
            return null;
    }
};

export const getPlatformValue = (platform: string): string => {
    switch (platform) {
        case 'instagram':
        case 'instagram_post':
            return 'IG Post';
        case 'instagram_story':
            return 'IG Story';
        case 'instagram_reels':
            return 'IG Reel';
        case 'instagram_tv':
            return 'IG TV';
        case 'facebook':
            return 'Facebook Post';
        case 'facebook_live':
            return 'Facebook Live';
        case 'facebook_story':
            return 'Facebook Story';
        case 'tiktok':
            return 'TT Post';
        case 'tiktok_story':
            return 'TT Story';
        case 'tiktok_mirror':
            return 'TT Mirroring';
        case 'podcast':
            return 'Podcast';
        case 'youtube':
            return 'YT Video';
        case 'youtube_shorts':
            return 'YT Short';
        case 'youtube_segment':
            return 'YT Segment';
        case 'youtube_integration':
            return 'YT Integration';
        case 'youtube_placement':
            return 'YT Placement';
        case 'youtube_live':
            return 'YT Live';
        case 'twitter':
        case 'twitter_post':
            return 'X Post';
        case 'blog':
            return 'Blog Post';
        case 'twitch':
            return 'Twitch';
        case 'linkedin':
            return 'LI Post';
        case 'dm':
            return 'DM';
        case 'dm_live':
            return 'DM Live';
        default:
            return '';
    }
};

export const getFileType = (fileName: string, path?: string, isLink?: string) => {
    const extension = (fileName?.split('.').pop() || '').toLowerCase();

    if (isLink) return { type: 'link', imgSrc: '/tempAssets/videoPlaceholder.svg' };

    switch (extension) {
        case 'pdf':
            return { type: 'pdf', iconClass: 'fi fi-rr-file-pdf text-danger', imgSrc: '/icons/files/file-pdf.svg' };
        case 'doc':
        case 'docx':
            return { type: 'word', iconClass: 'fi fi-rr-file-word text-primary', imgSrc: '/icons/files/file-word.svg' };
        case 'xls':
        case 'xlsx':
            return {
                type: 'excel',
                iconClass: 'fi fi-rr-file-excel text-success',
                imgSrc: '/icons/files/file-excel.svg',
            };
        case 'ppt':
        case 'pptx':
            return {
                type: 'powerpoint',
                iconClass: 'fi fi-rr-file-powerpoint text-warning',
                imgSrc: '/icons/files/file-powerpoint.svg',
            };
        case 'jpg':
        case 'png':
        case 'jpeg':
            return { type: 'image', iconClass: 'fi fi-rr-picture text-muted', imgSrc: path };
        case 'mp4':
            return { type: 'video', iconClass: 'fi fi-rr-file-video text-muted', imgSrc: path };
        case 'm4a':
        case 'mp3':
            return {
                type: 'audio',
                iconClass: 'fi fi-rr-file-audio text-muted',
                imgSrc: '/icons/files/file-audio.svg',
            };
        default:
            return { type: 'other', iconClass: 'fi fi-rr-file text-muted', imgSrc: '/icons/files/file.svg' };
    }
};

export function buildAclGroups<T extends IAdminAclGroup | IBrandAclGroup>(groups: T[], allValues: string[]): T[] {
    const grouped = new Set(groups?.flatMap(g => g.items));
    const ungrouped = allValues?.filter(v => !grouped?.has(v));

    if (process.env.NODE_ENV !== 'production' && ungrouped?.length) {
        console.warn('[Acl] Ungrouped permissions — add them to the groups config:', ungrouped);
    }

    const visibleGroups = groups
        .map(g => ({
            ...g,
            items: g.items.filter(i => allValues?.includes(i)),
        }))
        .filter(g => g?.items?.length > 0);

    if (ungrouped?.length) {
        visibleGroups.push({ key: 'other', label: 'Other', items: ungrouped } as T);
    }

    return visibleGroups;
}
